'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { getIdToken } from 'firebase/auth'
import { useTranslation } from 'react-i18next'
import { ClipLoader } from 'react-spinners'
import { TbSignature } from 'react-icons/tb'
import { MdTransgender, MdPsychology, MdFace } from 'react-icons/md'
import { FaHeart, FaSave, FaUndo, FaTimes } from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'

// Preset picks; anything else is entered via "Custom" (a short free-text input),
// mirroring the character-creation modal. Kept deliberately small.
const GENDERS = ['Male', 'Female']
const SEXUALITIES = ['Straight', 'Gay', 'Lesbian']
const CUSTOM_MAX = 30
const TEXT_MAX = 150

// "My Persona" — lets the signed-in user define who THEY are in chat (name,
// gender, sexuality, a short bio, and appearance). Saved to their users/{uid}
// doc and fed into the chat system prompt so characters know who they're talking
// to instead of only seeing a bare username. Rendered as the content of a shared
// <Modal>, so it owns its own Save button (the Modal footer auto-closes).
const MyPersona = ({ isOpen, closeModal }) => {
  const { user, username, displayProfilePicture, persona, setPersona } =
    useAuth()
  const router = useRouter()
  const { t } = useTranslation()

  const [name, setName] = useState('')
  const [gender, setGender] = useState('')
  const [customGender, setCustomGender] = useState('')
  const [sexuality, setSexuality] = useState('')
  const [customSexuality, setCustomSexuality] = useState('')
  const [bio, setBio] = useState('')
  const [appearance, setAppearance] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Snapshot the saved persona into the form each time the modal opens, so it
  // always reflects the latest saved values and discards unsaved edits from a
  // previous cancel. Every descriptive field is opt-in (blank by default): an
  // untouched persona injects nothing, so chats behave exactly as before the
  // persona feature existed. Name is the one convenience prefill (your username)
  // — on its own it never changes the chat, since that is already the {user} the
  // model saw.
  useEffect(() => {
    if (!isOpen) return
    setName(persona?.name ?? username ?? '')

    // Rehydrate gender/sexuality: a saved value that isn't one of the presets is
    // a custom entry, so switch the control into Custom mode showing that text.
    const savedGender = persona?.gender ?? ''
    if (savedGender && !GENDERS.includes(savedGender)) {
      setGender('Custom')
      setCustomGender(savedGender)
    } else {
      setGender(savedGender)
      setCustomGender('')
    }

    const savedSexuality = persona?.sexuality ?? ''
    if (savedSexuality && !SEXUALITIES.includes(savedSexuality)) {
      setSexuality('Custom')
      setCustomSexuality(savedSexuality)
    } else {
      setSexuality(savedSexuality)
      setCustomSexuality('')
    }

    setBio(persona?.bio ?? '')
    setAppearance(persona?.appearance ?? '')
    setError('')
    // Intentionally snapshots persona/username only when the modal opens, so
    // in-progress edits are never clobbered by a background user-info refresh.
  }, [isOpen])

  const savePersona = async () => {
    if (!user) {
      closeModal?.()
      router.push('/signup')
      return
    }
    if (saving) return

    setSaving(true)
    setError('')

    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/update_persona`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            name: name.trim(),
            gender: gender === 'Custom' ? customGender.trim() : gender,
            sexuality:
              sexuality === 'Custom' ? customSexuality.trim() : sexuality,
            bio: bio.trim(),
            appearance: appearance.trim(),
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        setPersona(data.persona)
        closeModal?.()
      } else if (response.status === 300) {
        setError(
          t('personaBannedWord', {
            defaultValue:
              'Banned word detected. Please adjust your persona and try again.',
          }),
        )
      } else {
        setError(
          data?.error ||
            t('somethingWentWrong', {
              defaultValue: 'Something went wrong. Please try again.',
            }),
        )
      }
    } catch (err) {
      console.error('Failed to save persona.', err)
      setError(
        t('somethingWentWrong', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      )
    } finally {
      setSaving(false)
    }
  }

  const avatarLetter = (name || username || '?').charAt(0).toUpperCase()

  // Factory defaults: name = your account username, every descriptive field
  // blank. resetToDefaults restores them; the Reset button only appears once the
  // form actually differs from them (i.e. you've customised something).
  const defaultName = username ?? ''
  const isAtDefaults =
    name === defaultName &&
    gender === '' &&
    sexuality === '' &&
    bio === '' &&
    appearance === ''

  const resetToDefaults = () => {
    setName(defaultName)
    setGender('')
    setCustomGender('')
    setSexuality('')
    setCustomSexuality('')
    setBio('')
    setAppearance('')
    setError('')
  }

  return (
    <div className="w-full max-w-xl mx-auto">
      <p className="text-sm text-gray-400 leading-relaxed mb-4">
        {t('personaIntro', {
          defaultValue:
            'This is you. Tell your characters who they are talking to — it shapes how they treat you in every chat.',
        })}
      </p>

      {/* Identity — the account avatar sits next to your name, which makes the
          "uses your account picture" note redundant, so it's gone. */}
      <div className="flex items-center gap-3 mb-4">
        {displayProfilePicture ? (
          <img
            src={displayProfilePicture}
            alt={name || username || 'You'}
            className="w-14 h-14 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[var(--foreground-bg-color)] flex items-center justify-center text-white text-xl font-semibold shrink-0">
            {avatarLetter}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <label className="block text-white text-sm font-semibold mb-2 flex items-center">
            <TbSignature color="#FFF" className="mr-2" size={12} />
            {t('personaNameLabel', { defaultValue: 'Your name' })}
          </label>
          <input
            type="text"
            id="personaName"
            placeholder={
              username ||
              t('personaNamePlaceholder', { defaultValue: 'Enter a name' })
            }
            value={name}
            maxLength={30}
            onChange={(e) => setName(e.target.value)}
            className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none"
          />
        </div>
      </div>

      {/* Gender + Sexuality — side by side to keep the form condensed. */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-white text-sm font-semibold mb-2 flex items-center">
            <MdTransgender color="#FFF" className="mr-2" size={12} />
            {t('personaGenderLabel', { defaultValue: 'Your gender' })}
          </label>
          {gender !== 'Custom' ? (
            <select
              id="personaGender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none appearance-none text-start cursor-pointer"
            >
              <option value="">
                {t('personaPreferNotToSay', {
                  defaultValue: 'Prefer not to say',
                })}
              </option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
              <option value="Custom">
                {t('custom', { defaultValue: 'Custom' })}
              </option>
            </select>
          ) : (
            <div className="flex items-center">
              <input
                type="text"
                value={customGender}
                maxLength={CUSTOM_MAX}
                placeholder={t('personaGenderPlaceholder', {
                  defaultValue: 'Enter gender',
                })}
                onChange={(e) => setCustomGender(e.target.value)}
                className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  setGender('')
                  setCustomGender('')
                }}
                className="ml-2 shrink-0 text-gray-400 hover:text-white"
                aria-label={t('cancel', { defaultValue: 'Cancel' })}
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-white text-sm font-semibold mb-2 flex items-center">
            <FaHeart color="#FFF" className="mr-2" size={12} />
            {t('personaSexualityLabel', { defaultValue: 'Your sexuality' })}
          </label>
          {sexuality !== 'Custom' ? (
            <select
              id="personaSexuality"
              value={sexuality}
              onChange={(e) => setSexuality(e.target.value)}
              className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none appearance-none text-start cursor-pointer"
            >
              <option value="">
                {t('personaPreferNotToSay', {
                  defaultValue: 'Prefer not to say',
                })}
              </option>
              {SEXUALITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              <option value="Custom">
                {t('custom', { defaultValue: 'Custom' })}
              </option>
            </select>
          ) : (
            <div className="flex items-center">
              <input
                type="text"
                value={customSexuality}
                maxLength={CUSTOM_MAX}
                placeholder={t('personaSexualityPlaceholder', {
                  defaultValue: 'Enter sexuality',
                })}
                onChange={(e) => setCustomSexuality(e.target.value)}
                className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  setSexuality('')
                  setCustomSexuality('')
                }}
                className="ml-2 shrink-0 text-gray-400 hover:text-white"
                aria-label={t('cancel', { defaultValue: 'Cancel' })}
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* About you */}
      <div className="mb-4">
        <label className="block text-white text-sm font-semibold mb-2 flex items-center">
          <MdPsychology color="#FFF" className="mr-2" size={12} />
          {t('personaBioLabel', { defaultValue: 'About you' })}
          <span className="ml-2 text-gray-500 text-xs font-normal">
            {t('optional', { defaultValue: 'Optional' })}
          </span>
        </label>
        <textarea
          id="personaBio"
          placeholder={t('personaBioPlaceholder', {
            defaultValue:
              'A kind stranger who loves late-night conversations...',
          })}
          value={bio}
          maxLength={TEXT_MAX}
          onChange={(e) => setBio(e.target.value)}
          className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none h-16 resize-none"
        />
      </div>

      {/* Appearance */}
      <div className="mb-4">
        <label className="block text-white text-sm font-semibold mb-2 flex items-center">
          <MdFace color="#FFF" className="mr-2" size={12} />
          {t('personaAppearanceLabel', { defaultValue: 'Your appearance' })}
          <span className="ml-2 text-gray-500 text-xs font-normal">
            {t('optional', { defaultValue: 'Optional' })}
          </span>
        </label>
        <textarea
          id="personaAppearance"
          placeholder={t('personaAppearancePlaceholder', {
            defaultValue: 'Tall, dark hair, leather jacket...',
          })}
          value={appearance}
          maxLength={TEXT_MAX}
          onChange={(e) => setAppearance(e.target.value)}
          className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none h-16 resize-none"
        />
      </div>

      {error && (
        <p className="text-sm text-center text-red-400 mb-3">{error}</p>
      )}

      {!isAtDefaults && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={resetToDefaults}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors duration-200"
          >
            <FaUndo size={11} />
            {t('personaReset', { defaultValue: 'Reset to default' })}
          </button>
        </div>
      )}

      <button
        onClick={savePersona}
        disabled={saving}
        className="bg-[var(--main-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50"
      >
        {saving ? <ClipLoader size={16} color="#fff" /> : <FaSave size={14} />}
        {t('save', { defaultValue: 'Save' })}
      </button>
    </div>
  )
}

export default MyPersona
